import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CrewMemberCreateInput,
  CrewMemberUpdateInput,
  CrewMemberWithUser,
  CrewUtilizationResponse,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

/** Shared query options so a prefetch warms the exact entry the hook reads. */
export function crewOptions() {
  return queryOptions({
    queryKey: ['crew'],
    queryFn: () => apiCall<{ data: CrewMemberWithUser[] }>('/crew'),
  });
}

export function useCrew() {
  return useQuery(crewOptions());
}

export interface CrewCandidate {
  id: string;
  display_name: string;
  email: string | null;
}

export function useCrewCandidates(enabled = true) {
  return useQuery<{ data: CrewCandidate[] }>({
    queryKey: ['crew', 'candidates'],
    queryFn: () => apiCall<{ data: CrewCandidate[] }>('/crew/candidates'),
    enabled,
  });
}

function invalidateCrew(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['crew'] });
  qc.invalidateQueries({ queryKey: ['crew-utilization'] });
}

export function useCreateCrewMember() {
  const qc = useQueryClient();
  return useMutation<CrewMemberWithUser, Error, CrewMemberCreateInput>({
    mutationFn: (body) =>
      apiCall<CrewMemberWithUser>('/crew', { method: 'POST', body }),
    onSuccess: () => invalidateCrew(qc),
  });
}

export function useUpdateCrewMember() {
  const qc = useQueryClient();
  return useMutation<
    CrewMemberWithUser,
    Error,
    { id: string; body: CrewMemberUpdateInput }
  >({
    mutationFn: ({ id, body }) =>
      apiCall<CrewMemberWithUser>(`/crew/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateCrew(qc),
  });
}

/** Soft-remove from the roster (history preserved; re-add reactivates). */
export function useRemoveCrewMember() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiCall<void>(`/crew/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidateCrew(qc);
      qc.invalidateQueries({ queryKey: ['crew', 'candidates'] });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
  });
}

export function useCrewUtilization(fromDate: string, toDate: string) {
  return useQuery<CrewUtilizationResponse>({
    queryKey: ['crew-utilization', fromDate, toDate],
    queryFn: () =>
      apiCall<CrewUtilizationResponse>(
        `/crew/utilization?from_date=${fromDate}&to_date=${toDate}`,
      ),
    staleTime: 30_000,
  });
}
